import React, { useState, useEffect, useMemo } from "react";
import axios from "axios";
import { FileBarChart2, Printer, RefreshCw, TrendingUp, TrendingDown, AlertTriangle, ChevronDown, ChevronRight, Lock } from "lucide-react";
import { toast } from "sonner";
import jsPDF from "jspdf";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const fmt = (v) => `R$ ${Number(v || 0).toFixed(2).replace(".", ",")}`;
const fmtBR = (d) => d ? d.split("-").reverse().join("/") : "—";

/**
 * Relatório de Receitas e Despesas por Centro de Custo.
 * Formato clássico "livro-caixa": saldo anterior + entradas + saídas + saldo final.
 */
export const CostCenterReport = ({ costCenters = [], currentLoja }) => {
  const [ccId, setCcId] = useState("");
  const today = new Date().toISOString().slice(0, 10);
  const monthAgo = new Date(Date.now() - 90 * 86400000).toISOString().slice(0, 10);
  const [start, setStart] = useState(monthAgo);
  const [end, setEnd] = useState(today);
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const params = { start, end };
      if (ccId) params.cc_id = ccId;
      const r = await axios.get(`${BACKEND_URL}/api/reports/cost-center`, { params });
      setReports(r.data.reports || []);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erro ao carregar relatório");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const exportPDF = () => {
    if (!reports.length) return;
    const doc = new jsPDF({ unit: "pt", format: "a4" });
    let y = 40;
    const gold = [212, 175, 55];
    const dark = [30, 30, 30];

    reports.forEach((rep, idx) => {
      if (idx > 0) { doc.addPage(); y = 40; }
      // Cabeçalho
      doc.setFontSize(14); doc.setFont("helvetica", "bold"); doc.setTextColor(...gold);
      doc.text("RECEITAS E DESPESAS POR CATEGORIA", 300, y, { align: "center" });
      y += 18;
      doc.setFontSize(10); doc.setTextColor(...dark); doc.setFont("helvetica", "normal");
      doc.text(`Caixa Geral - Centro de Custo: ${rep.cost_center.code} — ${rep.cost_center.name}`, 300, y, { align: "center" });
      y += 14;
      doc.setFontSize(9); doc.setTextColor(120, 120, 120);
      doc.text(`Período: ${fmtBR(rep.period.start)} - ${fmtBR(rep.period.end)}`, 300, y, { align: "center" });
      y += 24;

      doc.setDrawColor(...gold); doc.setLineWidth(0.5);
      doc.line(40, y, 555, y); y += 14;

      // Saldo anterior
      doc.setFontSize(10); doc.setTextColor(...dark); doc.setFont("helvetica", "bold");
      doc.text("Saldo anterior", 40, y);
      doc.text(fmt(rep.saldo_anterior), 555, y, { align: "right" });
      y += 20;

      // ENTRADAS por categoria
      doc.setFont("helvetica", "bold"); doc.setTextColor(...gold);
      doc.text("ENTRADAS", 40, y); y += 14;
      const entGroups = rep.entradas_por_categoria || [];
      if (entGroups.length === 0) {
        doc.setFont("helvetica", "italic"); doc.setFontSize(9); doc.setTextColor(120,120,120);
        doc.text("Sem entradas no período.", 50, y); y += 12;
      } else {
        entGroups.forEach((g) => {
          if (y > 770) { doc.addPage(); y = 40; }
          doc.setFont("helvetica", "bold"); doc.setFontSize(9); doc.setTextColor(...dark);
          doc.text(`${g.category}`, 45, y);
          doc.text(fmt(g.total), 555, y, { align: "right" });
          y += 12;
          doc.setFont("helvetica", "normal"); doc.setFontSize(8); doc.setTextColor(90,90,90);
          g.items.forEach((it) => {
            if (y > 780) { doc.addPage(); y = 40; }
            doc.text(`  • ${(it.date || "").slice(5)} ${(it.description || "—").slice(0, 55)}`, 55, y);
            doc.text(fmt(it.amount), 555, y, { align: "right" });
            y += 10;
          });
          y += 4;
        });
      }
      doc.setFont("helvetica", "bold"); doc.setFontSize(10); doc.setTextColor(...dark);
      doc.line(40, y - 2, 555, y - 2);
      doc.text("Total de entradas", 40, y + 10);
      doc.text(fmt(rep.total_entradas), 555, y + 10, { align: "right" });
      y += 26;

      // SAÍDAS por categoria
      doc.setFont("helvetica", "bold"); doc.setTextColor(220, 70, 70);
      doc.text("SAÍDAS", 40, y); y += 14;
      const outGroups = rep.saidas_por_categoria || [];
      if (outGroups.length === 0) {
        doc.setFont("helvetica", "italic"); doc.setFontSize(9); doc.setTextColor(120,120,120);
        doc.text("Sem saídas no período.", 50, y); y += 12;
      } else {
        outGroups.forEach((g) => {
          if (y > 770) { doc.addPage(); y = 40; }
          doc.setFont("helvetica", "bold"); doc.setFontSize(9); doc.setTextColor(...dark);
          doc.text(`${g.category}`, 45, y);
          doc.text(`- ${fmt(g.total)}`, 555, y, { align: "right" });
          y += 12;
          doc.setFont("helvetica", "normal"); doc.setFontSize(8); doc.setTextColor(90,90,90);
          g.items.forEach((it) => {
            if (y > 780) { doc.addPage(); y = 40; }
            doc.text(`  • ${(it.date || "").slice(5)} ${(it.description || "—").slice(0, 55)}`, 55, y);
            doc.text(`- ${fmt(it.amount)}`, 555, y, { align: "right" });
            y += 10;
          });
          y += 4;
        });
      }
      doc.setFont("helvetica", "bold"); doc.setFontSize(10); doc.setTextColor(...dark);
      doc.line(40, y - 2, 555, y - 2);
      doc.text("Total de saídas", 40, y + 10);
      doc.text(`- ${fmt(rep.total_saidas)}`, 555, y + 10, { align: "right" });
      y += 30;

      // Saldos
      doc.setFontSize(11); doc.setTextColor(...gold);
      doc.text("Saldo do período", 40, y);
      doc.text(fmt(rep.saldo_periodo), 555, y, { align: "right" });
      y += 18;
      doc.setFontSize(12); doc.setTextColor(...dark);
      doc.text("Saldo final", 40, y);
      doc.text(fmt(rep.saldo_final), 555, y, { align: "right" });
      y += 30;

      // ATRASOS
      if ((rep.atrasos || []).length > 0) {
        if (y > 720) { doc.addPage(); y = 40; }
        doc.setFontSize(11); doc.setTextColor(220, 140, 40); doc.setFont("helvetica", "bold");
        doc.text(rep.cost_center.is_multa ? "MULTAS PREVISTAS (atrasos vencidos)" : "EM ATRASO — Previsão via Rateio", 40, y);
        doc.text(fmt(rep.total_atrasos), 555, y, { align: "right" });
        y += 14;
        doc.setFontSize(8); doc.setTextColor(90,90,90); doc.setFont("helvetica", "normal");
        rep.atrasos.forEach((a) => {
          if (y > 780) { doc.addPage(); y = 40; }
          doc.text(`  • CIM ${a.cim} — ${(a.member_name || "").slice(0,32)} · ${a.month} · vence ${fmtBR(a.due_date)}`, 50, y);
          doc.text(fmt(a.projected_amount), 555, y, { align: "right" });
          y += 10;
        });
      }

      // Rodapé
      doc.setFontSize(8); doc.setTextColor(150, 150, 150);
      doc.text(`${currentLoja?.name || "Loja Maçônica"} — Lojas Integradas`, 300, 820, { align: "center" });
    });

    doc.save(`Relatorio-CC-${reports[0].cost_center.name}-${end}.pdf`);
    toast.success("PDF gerado com sucesso");
  };

  return (
    <div className="space-y-6" data-testid="cost-center-report">
      <div className="luxury-card p-5">
        <div className="flex items-center justify-between gap-3 flex-wrap mb-4">
          <div>
            <h3 className="font-serif font-bold text-lg text-white flex items-center gap-2">
              <FileBarChart2 className="w-5 h-5 text-[#D4AF37]" />
              Relatório por Centro de Custo
            </h3>
            <p className="text-xs text-slate-400 mt-0.5">
              Livro-caixa clássico: entradas e saídas de cada CC, incluindo rateios automáticos.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={load}
              disabled={loading}
              className="border border-slate-700 hover:border-[#D4AF37] text-slate-300 hover:text-white px-3 py-2 rounded-xl text-xs font-semibold flex items-center gap-1.5 transition-colors"
              data-testid="btn-refresh-report"
            >
              <RefreshCw className={`w-3.5 h-3.5 ${loading ? "animate-spin" : ""}`} />
              Atualizar
            </button>
            <button
              onClick={exportPDF}
              disabled={loading || !reports.length}
              className="bg-[#D4AF37] hover:bg-[#C5A028] disabled:opacity-40 text-slate-950 font-bold px-3 py-2 rounded-xl text-xs uppercase flex items-center gap-1.5"
              data-testid="btn-export-pdf"
            >
              <Printer className="w-3.5 h-3.5" />
              Exportar PDF
            </button>
          </div>
        </div>

        {/* Filtros */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <div>
            <label className="block text-[10px] uppercase tracking-widest text-slate-400 font-bold mb-1">Centro de Custo</label>
            <select
              value={ccId}
              onChange={(e) => setCcId(e.target.value)}
              className="w-full bg-[#080C14] border border-slate-700 rounded-lg px-3 py-2 text-xs text-white"
              data-testid="filter-cc-select"
            >
              <option value="">Todos os CCs (relatório completo)</option>
              {costCenters.map((c) => (
                <option key={c.id} value={c.id}>{c.name}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-[10px] uppercase tracking-widest text-slate-400 font-bold mb-1">Início</label>
            <input
              type="date"
              value={start}
              onChange={(e) => setStart(e.target.value)}
              className="w-full bg-[#080C14] border border-slate-700 rounded-lg px-3 py-2 text-xs text-white"
              data-testid="filter-start"
            />
          </div>
          <div>
            <label className="block text-[10px] uppercase tracking-widest text-slate-400 font-bold mb-1">Fim</label>
            <input
              type="date"
              value={end}
              onChange={(e) => setEnd(e.target.value)}
              className="w-full bg-[#080C14] border border-slate-700 rounded-lg px-3 py-2 text-xs text-white"
              data-testid="filter-end"
            />
          </div>
        </div>
      </div>

      {/* Relatórios */}
      {loading && <div className="text-center text-slate-400 text-sm italic py-8">Carregando relatório…</div>}

      {!loading && reports.length === 0 && (
        <div className="luxury-card p-12 text-center">
          <FileBarChart2 className="w-12 h-12 text-slate-600 mx-auto mb-3" />
          <p className="text-slate-400 text-sm">Nenhum dado no período selecionado.</p>
        </div>
      )}

      {!loading && reports.map((rep) => (
        <div key={rep.cost_center.id} className="luxury-card p-6 space-y-4" data-testid={`report-block-${rep.cost_center.id}`}>
          <div className="text-center border-b border-[#D4AF37]/30 pb-4">
            <h4 className="text-sm uppercase tracking-widest text-[#D4AF37] font-bold">RECEITAS E DESPESAS POR CATEGORIA</h4>
            <p className="text-lg text-white font-serif mt-1 flex items-center justify-center gap-2">
              {rep.cost_center.is_multa && <Lock className="w-4 h-4 text-rose-400" />}
              <span className="text-[#D4AF37] font-mono text-sm">{rep.cost_center.code}</span>
              <span>—</span>
              <span>{rep.cost_center.name}</span>
            </p>
            <p className="text-xs text-slate-400 mt-0.5">Período: {fmtBR(rep.period.start)} — {fmtBR(rep.period.end)}</p>
          </div>

          {/* Saldo anterior */}
          <div className="flex justify-between items-center px-4 py-2 rounded bg-slate-800/50">
            <span className="text-sm font-semibold text-slate-300">Saldo anterior</span>
            <span className="font-mono font-bold text-white">{fmt(rep.saldo_anterior)}</span>
          </div>

          {/* ENTRADAS por categoria */}
          <CategorizedSection
            title="ENTRADAS"
            icon={<TrendingUp className="w-4 h-4" />}
            color="emerald"
            groups={rep.entradas_por_categoria || []}
            total={rep.total_entradas}
            emptyLabel="Sem entradas no período."
          />

          {/* SAÍDAS por categoria */}
          <CategorizedSection
            title="SAÍDAS"
            icon={<TrendingDown className="w-4 h-4" />}
            color="rose"
            groups={rep.saidas_por_categoria || []}
            total={rep.total_saidas}
            emptyLabel="Sem saídas no período."
            negative
          />

          {/* Saldos finais */}
          <div className="pt-3 border-t border-[#D4AF37]/30 space-y-2">
            <div className="flex justify-between items-center">
              <span className="text-sm text-slate-300 font-semibold">Saldo do período</span>
              <span className={`font-mono font-bold ${rep.saldo_periodo >= 0 ? "text-emerald-400" : "text-rose-400"}`}>
                {fmt(rep.saldo_periodo)}
              </span>
            </div>
            <div className="flex justify-between items-center bg-[#D4AF37]/10 border border-[#D4AF37]/40 rounded-lg px-4 py-3">
              <span className="text-base text-[#D4AF37] font-serif font-bold">Saldo final</span>
              <span className="font-mono font-bold text-lg text-[#D4AF37]">{fmt(rep.saldo_final)}</span>
            </div>
          </div>

          {/* ATRASOS (previsão) — sempre visível para transparência */}
          <OverdueSection
            atrasos={rep.atrasos || []}
            total={rep.total_atrasos || 0}
            isMulta={rep.cost_center.is_multa}
          />
        </div>
      ))}
    </div>
  );
};

/** Seção de entradas/saídas agrupada por categoria com toggle expand/collapse. */
const CategorizedSection = ({ title, icon, color, groups, total, emptyLabel, negative = false }) => {
  const [openCats, setOpenCats] = useState({});
  const toggle = (cat) => setOpenCats((p) => ({ ...p, [cat]: !p[cat] }));
  const colorText = color === "emerald" ? "text-emerald-400" : "text-rose-400";
  const colorBorder = color === "emerald" ? "border-emerald-500/20" : "border-rose-500/20";
  const sign = negative ? "- " : "";

  return (
    <div>
      <div className={`flex items-center gap-2 ${colorText} font-bold text-sm mb-2`}>
        {icon} {title}
      </div>
      {groups.length === 0 ? (
        <div className="text-xs text-slate-500 italic pl-6">{emptyLabel}</div>
      ) : (
        <div className="space-y-1.5">
          {groups.map((g) => {
            const open = openCats[g.category];
            return (
              <div key={g.category} className={`rounded-lg border ${colorBorder} bg-[#080C14]/40`}>
                <button
                  type="button"
                  onClick={() => toggle(g.category)}
                  className="w-full flex justify-between items-center px-3 py-2 hover:bg-slate-800/40 transition-colors"
                  data-testid={`cat-toggle-${g.category}`}
                >
                  <span className="flex items-center gap-1.5 text-xs text-slate-200 font-semibold">
                    {open ? <ChevronDown className="w-3.5 h-3.5" /> : <ChevronRight className="w-3.5 h-3.5" />}
                    {g.category}
                    <span className="text-[10px] text-slate-500 font-normal">({g.items.length})</span>
                  </span>
                  <span className={`font-mono font-bold text-xs ${colorText}`}>{sign}{fmt(g.total)}</span>
                </button>
                {open && (
                  <div className="border-t border-slate-800 divide-y divide-slate-800/60">
                    {g.items.map((it, i) => (
                      <div key={i} className="flex justify-between px-6 py-1.5 text-[11px] text-slate-400">
                        <span className="truncate max-w-[70%]">
                          <span className="text-slate-500 mr-1.5 font-mono">{it.date}</span>
                          {it.description}
                        </span>
                        <span className={`font-mono ${colorText}`}>{sign}{fmt(it.amount)}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
      <div className="flex justify-between items-center mt-2 pt-2 border-t border-slate-800 font-bold text-sm">
        <span className="text-white">Total de {title.toLowerCase()}</span>
        <span className={`font-mono ${colorText}`}>{sign}{fmt(total)}</span>
      </div>
    </div>
  );
};

/** Seção de atrasos: mensalidades vencidas e ainda não pagas com projeção de rateio. */
const OverdueSection = ({ atrasos, total, isMulta }) => {
  const [open, setOpen] = useState(false);
  const isEmpty = !atrasos || atrasos.length === 0;
  const borderColor = isEmpty ? "border-slate-700" : (isMulta ? "border-rose-500/40" : "border-amber-500/40");
  const bgColor = isEmpty ? "bg-slate-800/20" : (isMulta ? "bg-rose-500/5" : "bg-amber-500/5");
  const textColor = isEmpty ? "text-slate-400" : (isMulta ? "text-rose-300" : "text-amber-300");
  const title = isMulta ? "MULTAS PREVISTAS (atrasos vencidos)" : "EM ATRASO — Inadimplência (previsão)";

  return (
    <div className={`rounded-lg border ${borderColor} ${bgColor} p-3`} data-testid="report-atrasos">
      <button
        type="button"
        onClick={() => !isEmpty && setOpen((v) => !v)}
        disabled={isEmpty}
        className="w-full flex justify-between items-center disabled:cursor-default"
      >
        <span className={`flex items-center gap-2 font-bold text-sm ${textColor}`}>
          <AlertTriangle className="w-4 h-4" />
          {title}
          <span className="text-[10px] text-slate-400 font-normal">({atrasos.length})</span>
        </span>
        <div className="flex items-center gap-2">
          <span className={`font-mono font-bold text-sm ${textColor}`}>{fmt(total)}</span>
          {!isEmpty && (open ? <ChevronDown className="w-4 h-4 text-slate-400" /> : <ChevronRight className="w-4 h-4 text-slate-400" />)}
        </div>
      </button>
      {isEmpty && (
        <p className="text-[10px] text-slate-500 italic mt-2">
          ✓ Nenhum Irmão inadimplente projetado para este CC.
          {!isMulta && " Configure as Regras de Rateio (aba Capitações) para ver a projeção automática."}
        </p>
      )}
      {open && !isEmpty && (
        <div className="mt-3 border-t border-slate-800 pt-2 space-y-1">
          <div className="grid grid-cols-12 text-[10px] uppercase tracking-widest text-slate-500 font-bold pb-1 border-b border-slate-800">
            <div className="col-span-4">Irmão</div>
            <div className="col-span-2">CIM</div>
            <div className="col-span-2">Mês</div>
            <div className="col-span-2">Vencimento</div>
            <div className="col-span-2 text-right">Projetado</div>
          </div>
          {atrasos.map((a) => (
            <div key={a.fee_id} className="grid grid-cols-12 text-[11px] text-slate-300 py-1">
              <div className="col-span-4 truncate">{a.member_name}</div>
              <div className="col-span-2 font-mono text-slate-400">{a.cim}</div>
              <div className="col-span-2 text-slate-400">{a.month}</div>
              <div className="col-span-2 font-mono text-rose-300">{fmtBR(a.due_date)}</div>
              <div className={`col-span-2 text-right font-mono font-bold ${isMulta ? "text-rose-300" : "text-amber-300"}`}>{fmt(a.projected_amount)}</div>
            </div>
          ))}
          <p className="text-[10px] text-slate-500 italic mt-2">
            💡 Previsão: se todos esses irmãos quitarem hoje sem desconto (atrasados), este CC receberá {fmt(total)} via {isMulta ? "multa" : "Rateio"}.
          </p>
        </div>
      )}
    </div>
  );
};

export default CostCenterReport;
